import { Box, Image, Text, useColorModeValue } from "@chakra-ui/react";

/**
 * Photo with a tilted "polaroid" frame — white border + drop shadow,
 * rotates flat on hover. Used in the about page hero/grid and in the
 * partner landing PhotoGallery.
 *
 * The photo sits on a muted placeholder (an unloaded <img> is transparent),
 * so the white frame never shows as a blank white box while it loads. The
 * photo paints as soon as its bytes arrive, with no dependence on
 * hydration, so don't gate its visibility on JS state. Pass
 * `priority` for an above-the-fold photo (eager, high fetch priority), and
 * optionally `placeholder`, a tiny blurred data URI of the photo.
 */
export function Polaroid({
  src,
  alt,
  caption,
  rotate = "-1.5deg",
  ratio = "3/2",
  priority = false,
  placeholder,
  onError,
  ...props
}) {
  const frameBg = useColorModeValue("white", "gray.100");
  const captionColor = useColorModeValue("gray.700", "gray.800");
  const shadow = useColorModeValue(
    "0 10px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)",
    "0 10px 30px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.35)",
  );
  const placeholderBg = useColorModeValue("gray.200", "gray.600");
  return (
    <Box
      bg={frameBg}
      p={3}
      pb={caption ? 4 : 3}
      borderRadius="sm"
      transform={`rotate(${rotate})`}
      boxShadow={shadow}
      transition="transform 0.3s ease-out"
      _hover={{ transform: "rotate(0deg) translateY(-4px)" }}
      {...props}
    >
      <Box
        bg={placeholderBg}
        bgSize="cover"
        bgPosition="center"
        // Plain style: Chakra's bgImage rewrites url(...) as a gradient.
        style={
          placeholder ? { backgroundImage: `url("${placeholder}")` } : undefined
        }
        sx={{ aspectRatio: ratio }}
        overflow="hidden"
      >
        <Image
          src={src}
          alt={alt}
          w="100%"
          h="100%"
          objectFit="cover"
          display="block"
          loading={priority ? "eager" : "lazy"}
          fetchpriority={priority ? "high" : undefined}
          onError={onError}
        />
      </Box>
      {caption && (
        <Text
          mt={3}
          fontSize="sm"
          color={captionColor}
          textAlign="center"
          lineHeight="1.35"
        >
          {caption}
        </Text>
      )}
    </Box>
  );
}
